import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

const pluginVersionSchema = z
  .string()
  .trim()
  .regex(/^\d+\.\d+\.\d+(?:\.\d+)?$/);

/**
 * Opt-in switch env var: accepts the usual truthy/falsy spellings so ops can
 * set it however their tooling prefers. Validated at startup, so a typo fails
 * loudly instead of silently leaving a switch in its default state.
 */
const booleanFlagSchema = z
  .enum(['true', 'false', '1', '0', 'yes', 'no'])
  .optional();

const envSchema = createEnv({
  server: {
    // Database
    // DATABASE_URL is optional because staging/production can use Arkyv-owned
    // URLs instead of integration-owned Neon/Vercel variables.
    DATABASE_URL: z.url().optional(),
    DATABASE_URL_UNPOOLED: z.url().optional(),
    POSTGRES_URL: z.url().optional(),
    ARKYV_STAGING_DATABASE_URL: z.url().optional(),
    ARKYV_STAGING_DATABASE_URL_UNPOOLED: z.url().optional(),
    ARKYV_PRODUCTION_DATABASE_URL: z.url().optional(),
    ARKYV_PRODUCTION_DATABASE_URL_UNPOOLED: z.url().optional(),

    // Auth - REQUIRED
    BETTER_AUTH_SECRET: z.string().min(1),
    MICROSOFT_CLIENT_ID: z.string().min(1).optional(),
    MICROSOFT_CLIENT_SECRET: z.string().min(1).optional(),
    GOOGLE_CLIENT_ID: z.string().min(1).optional(),
    GOOGLE_CLIENT_SECRET: z.string().min(1).optional(),
    AUTODESK_CLIENT_ID: z.string().min(1).optional(),
    AUTODESK_CLIENT_SECRET: z.string().min(1).optional(),

    // Internal / staff — organization whose members are treated as Arkyv staff
    // (eligible for the staff-only role switcher). See lib/auth/staff-utils.ts.
    ARKYV_INTERNAL_ORGANIZATION_ID: z.string().uuid().optional(),

    // AI APIs
    AI_GATEWAY_API_KEY: z.string().min(1).optional(),
    TAVILY_API_KEY: z.string().min(1).optional(),
    VOYAGE_API_KEY: z.string().min(1).optional(),
    GEMINI_API_KEY: z.string().min(1).optional(),

    // Azure Document Intelligence
    AZURE_DOC_INTELLIGENCE_ENDPOINT: z.url().optional(),
    AZURE_DOC_INTELLIGENCE_API_KEY: z.string().min(1).optional(),

    // Email (Resend)
    RESEND_API_KEY: z.string().min(1).optional(),
    RESEND_WEBHOOK_SECRET: z.string().min(1).optional(),
    // Outbound send guard. Defaults to `allowlist` outside production so a
    // preview pointed at production-like data cannot mail real customers.
    COMMUNICATION_SEND_MODE: z
      .enum(['live', 'allowlist', 'dry_run'])
      .optional(),
    COMMUNICATION_TEST_RECIPIENTS: z.string().optional(),
    /**
     * Optional registered company address for lifecycle and marketing mail.
     * When absent, the address row is omitted from the rendered footer.
     */
    COMMUNICATION_COMPANY_ADDRESS: z.string().optional(),
    // Optional mail still requires an HTTPS privacy-notice URL at send time.
    COMMUNICATION_PRIVACY_URL: z.string().url().optional(),
    // Unsubscribe links never expire, so their signing key must outlive any
    // rotation of the auth secret. Falls back to BETTER_AUTH_SECRET until a
    // dedicated one is provisioned; previous keys stay valid for verification
    // so links already delivered keep working through a rotation.
    COMMUNICATION_UNSUBSCRIBE_SECRET: z.string().min(1).optional(),
    COMMUNICATION_UNSUBSCRIBE_SECRETS_PREVIOUS: z.string().optional(),

    // Billing (Stripe)
    STRIPE_SECRET_KEY: z.string().min(1).optional(),
    STRIPE_WEBHOOK_SECRET: z.string().min(1).optional(),
    // Local-only Stripe API endpoint used by end-to-end tests. Runtime code
    // ignores this unless APP_ENV=test so deployed environments cannot reroute
    // billing traffic accidentally.
    STRIPE_TEST_API_BASE_URL: z.url().optional(),
    STRIPE_CUSTOMER_PORTAL_CONFIGURATION_ID: z
      .string()
      .startsWith('bpc_')
      .optional(),
    STRIPE_TAX_APPROVED: z.enum(['true', 'false']).optional(),

    // Services
    CLOUDCONVERT_API_KEY: z.string().min(1).optional(),
    CRON_SECRET: z.string().min(1).optional(),
    // Explicit rollout gate; capture is always durable, execution is opt-in.
    CONTENT_DELETION_ENABLED: z.enum(['true', 'false']).optional(),
    // Explicit deployment-scoped migration ingress/storage barrier; default off.
    BLOB_MIGRATION_MAINTENANCE: z.enum(['true', 'false']).optional(),
    BOVERKET_SLACK_WEBHOOK_URL: z.url().optional(),
    BOVERKET_AUTOMATION_USER_ID: z.string().uuid().optional(),

    // PostHog (server-side for feature flags)
    POSTHOG_API_KEY: z.string().min(1).optional(),
    POSTHOG_HOST: z.string().optional(),
    // PostHog source-map uploads. Keep separate from POSTHOG_API_KEY, which is
    // the project token used by server-side feature flags.
    POSTHOG_PERSONAL_API_KEY: z.string().min(1).optional(),
    POSTHOG_PROJECT_ID: z.string().min(1).optional(),
    // Shared secret carried by PostHog Workflow webhook deliveries into the
    // communication event ingest endpoint. It is intentionally separate from
    // project and personal API keys: the receiver needs no PostHog API access.
    COMMUNICATION_POSTHOG_WEBHOOK_SECRET: z.string().min(16).optional(),

    // Meta Pixel / Conversions API
    META_PIXEL_ID: z.string().min(1).optional(),
    META_CONVERSIONS_API_TOKEN: z.string().min(1).optional(),

    // Separate stores: public branding/distribution assets and private user data.
    BLOB_READ_WRITE_TOKEN: z.string().optional(),
    PRIVATE_BLOB_READ_WRITE_TOKEN: z.string().optional(),
    PRIVATE_BLOB_STORE_ID: z.string().optional(),

    // Blob Migration
    BLOB_MIGRATION_OLD_STORE_PATTERN: z.string().optional(),
    BLOB_MIGRATION_NEW_STORE_PATTERN: z.string().optional(),

    // URLs
    APP_URL: z.url().optional(),
    BETTER_AUTH_URL: z.url().optional(),

    // Vercel (auto-injected)
    VERCEL_ENV: z.enum(['development', 'preview', 'production']).optional(),
    VERCEL_TARGET_ENV: z.string().optional(),
    VERCEL_BRANCH_URL: z.string().optional(),
    VERCEL_URL: z.string().optional(),
    VERCEL_GIT_COMMIT_REF: z.string().optional(),

    // App
    NODE_ENV: z
      .enum(['development', 'production', 'test'])
      .default('development'),
    APP_ENV: z
      .enum(['local', 'preview', 'staging', 'production', 'test'])
      .optional(),

    // Plugin auto-update hold-down switches (ENG-1257).
    //
    // Opt-in: unset means the client-side hold-down is OFF, so the swapper
    // keeps applying staged versions and we fail open. Enable per plugin (or
    // per platform) once the fleet is large enough to want the brake, without
    // shipping new binaries. Resolution is most-specific-first:
    // connector+platform, then connector, then the global switch. Server-side
    // verdicts (the autoUpdate kill switch and recommendedVersion) remain the
    // authoritative brake either way.
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED: booleanFlagSchema,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_REVIT: booleanFlagSchema,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_REVIT_WINDOWS: booleanFlagSchema,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_REVIT_MACOS: booleanFlagSchema,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_RHINO: booleanFlagSchema,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_RHINO_WINDOWS: booleanFlagSchema,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_RHINO_MACOS: booleanFlagSchema,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_ARCHICAD: booleanFlagSchema,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_ARCHICAD_WINDOWS: booleanFlagSchema,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_ARCHICAD_MACOS: booleanFlagSchema,

    // Runtime
    NEXT_RUNTIME: z.enum(['nodejs', 'edge']).optional(),

    // Debug
    DEBUG: z.string().optional(),
    PORT: z.string().optional(),

    // Testing
    PLAYWRIGHT_TEST_BASE_URL: z.string().optional(),
    PLAYWRIGHT: z.string().optional(),
    CI_PLAYWRIGHT: z.string().optional(),
    CI: z.string().optional(),
    API_BASE_URL: z.string().optional(),
    TEST_BASE_URL: z.string().optional(),
    TEST_RUN_ID: z.string().optional(),
  },

  client: {
    NEXT_PUBLIC_MICROSOFT_SSO_ENABLED: z.string().optional(),
    NEXT_PUBLIC_MICROSOFT_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_GOOGLE_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_BETTER_AUTH_URL: z.url().optional(),
    NEXT_PUBLIC_ENTERPRISE_CONTACT_URL: z.url().optional(),
    NEXT_PUBLIC_ARCHICAD_PLUGIN_RECOMMENDED_VERSION:
      pluginVersionSchema.optional(),
    // PostHog analytics
    NEXT_PUBLIC_POSTHOG_KEY: z.string().min(1).optional(),
    NEXT_PUBLIC_POSTHOG_HOST: z.string().optional(),
    NEXT_PUBLIC_META_PIXEL_ID: z.string().min(1).optional(),
    NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: z.string().min(1).optional(),
    NEXT_PUBLIC_VERCEL_ENV: z
      .enum(['development', 'preview', 'production'])
      .optional(),
    NEXT_PUBLIC_VERCEL_TARGET_ENV: z.string().optional(),
    NEXT_PUBLIC_VERCEL_URL: z.string().optional(),
    // App environment for analytics segmentation
    NEXT_PUBLIC_APP_ENV: z
      .enum(['local', 'preview', 'staging', 'production', 'test'])
      .optional(),
  },

  // For Next.js >= 13.4.4, only client-side variables need to be specified
  experimental__runtimeEnv: {
    NEXT_PUBLIC_MICROSOFT_SSO_ENABLED:
      process.env.NEXT_PUBLIC_MICROSOFT_SSO_ENABLED,
    NEXT_PUBLIC_MICROSOFT_CLIENT_ID:
      process.env.NEXT_PUBLIC_MICROSOFT_CLIENT_ID,
    NEXT_PUBLIC_GOOGLE_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
    NEXT_PUBLIC_BETTER_AUTH_URL: process.env.NEXT_PUBLIC_BETTER_AUTH_URL,
    NEXT_PUBLIC_ENTERPRISE_CONTACT_URL:
      process.env.NEXT_PUBLIC_ENTERPRISE_CONTACT_URL,
    NEXT_PUBLIC_ARCHICAD_PLUGIN_RECOMMENDED_VERSION:
      process.env.NEXT_PUBLIC_ARCHICAD_PLUGIN_RECOMMENDED_VERSION,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID,
    NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID:
      process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
    NEXT_PUBLIC_VERCEL_ENV:
      process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.VERCEL_ENV,
    NEXT_PUBLIC_VERCEL_TARGET_ENV:
      process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV ??
      process.env.VERCEL_TARGET_ENV,
    NEXT_PUBLIC_VERCEL_URL:
      process.env.NEXT_PUBLIC_VERCEL_URL ?? process.env.VERCEL_URL,
    NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
  },

  // Skip validation in edge runtime, during build, or in tests
  skipValidation:
    !!process.env.SKIP_ENV_VALIDATION ||
    process.env.npm_lifecycle_event === 'lint' ||
    process.env.APP_ENV === 'test',

  // Allow empty strings for optional vars
  emptyStringAsUndefined: true,
});

export const env = envSchema;
